import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { protocolDefaultOnDownload } from '../../../components/LazyList/LazyListHelpers';
import { HardwareStatus } from '../../../services/domainServices/HardwareService';
import {
  createDataResponse,
  createErrorResponse,
} from '../../../services/fetchServices/fetchWrapper';
import {
  getUserHardware,
  HardwareArrayType,
} from '../../../services/fetchServices/HardwareApi/getUserHardware';
import { getAssignProtocol } from '../../../services/fetchServices/UserApi/getAssignProtocol';
import { getUnassignProtocol } from '../../../services/fetchServices/UserApi/getUnassignProtocol';

export const useUserHardwareData = (ldapLogin: string) => {
  const [data, setData] = useState<HardwareArrayType>([]);
  const [isLoading, setIsLoading] = useState(false);

  const idsAndStatusRecord = (hardwareIds: number[], status: HardwareStatus) =>
    hardwareIds.reduce(
      (a: { [name: string]: HardwareStatus }, b: number) => ({
        ...a,
        [b]: status,
      }),
      {},
    );

  const refetchAssignedHardware = useCallback(async () => {
    setIsLoading(true);
    const userHardware = await getUserHardware(ldapLogin);
    if (userHardware.type === 'success') {
      setData(userHardware.data);
    } else {
      toast.error(userHardware.error.message);
    }
    setIsLoading(false);
  }, [ldapLogin]);

  useEffect(() => {
    refetchAssignedHardware();
  }, [refetchAssignedHardware]);

  // The hardware change is saved even when the protocol mail fails, so this is a warning.
  function warnWhenProtocolMailFailed(mailSent: boolean) {
    if (!mailSent) {
      toast.warning(
        'The protocol email was not sent. Send the downloaded protocol to the user.',
      );
    }
  }

  async function submitUnassignHardware(
    hardwareList: Map<number, HardwareStatus>,
    sendPrivateEmail: boolean,
    comment: string,
  ) {
    const res = await getUnassignProtocol(
      ldapLogin,
      new Date(),
      Object.fromEntries(hardwareList.entries()),
      sendPrivateEmail,
      comment,
    );

    if (res.type === 'success') {
      await refetchAssignedHardware();
      toast.success('Successfully unassigned hardware');
      warnWhenProtocolMailFailed(res.data.mailSent);
      try {
        protocolDefaultOnDownload(res.data.name)(res.data.protocol);
      } catch (err) {
        toast.error('Error downloading protocol');
      }
      return createDataResponse({});
    }
    toast.error(`Failed to unassign hardware: ${res.error.message}`);
    return createErrorResponse("Couldn't unassign hardware");
  }

  async function submitAssignHardware(
    hardwareIds: number[],
    sendPrivateEmail: boolean,
    comment: string,
  ) {
    const res = await getAssignProtocol(
      ldapLogin,
      new Date(),
      idsAndStatusRecord(hardwareIds, 'InUse'),
      sendPrivateEmail,
      comment,
    );
    if (res.type === 'success') {
      await refetchAssignedHardware();
      warnWhenProtocolMailFailed(res.data.mailSent);
      try {
        protocolDefaultOnDownload(res.data.name)(res.data.protocol);
      } catch (err) {
        toast.error("Couldn't download protocol");
      }
      return createDataResponse({});
    }
    toast.error(`Failed to assign hardware: ${res.error.message}`);
    return createErrorResponse("Couldn't assign hardware");
  }

  return {
    data,
    isLoading,
    setIsLoading,
    submitAssignHardware,
    submitUnassignHardware,
    refetchAssignedHardware,
  };
};
